// src/App.js
// The root of the React application.
// Sets up routing, wraps every page in the Layout component, and provides
// authentication context to the whole tree.
//
// Routing flow:
// 1. <AuthProvider> supplies the auth state (user, isLoggedIn, isAdmin) to everything.
// 2. <BrowserRouter> wraps the app and provides URL state to all descendants.
// 3. <Layout> renders Header above and Footer below the active page.
// 4. <Routes> picks the right page component based on the current URL.
// 5. Protected routes are wrapped in guards: ProtectedRoute (any logged-in user)
//    for /profile, and PrivateRoute (admin only) for /admin-dash.
// 6. The "*" route at the bottom catches anything that does not match and renders
//    NotFound. It must be last; React Router matches top-down.
//
// AuthProvider sits outside BrowserRouter because it does not use router hooks;
// it only needs to be above every component that calls useAuth (including the nav).

import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "./context/AuthProvider";
import Layout from "./components/Layout";
import Home from "./pages/Home";
import Blogs from "./pages/Blogs";
import SingleBlog from "./pages/SingleBlog";
import Projects from "./pages/Projects";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Login from "./pages/Login";
import SignUp from "./pages/SignUp";
import Profile from "./pages/Profile";
import AdminDashboard from "./pages/AdminDashboard";
import NotFound from "./pages/NotFound";
import ProtectedRoute from "./pages/ProtectedRoute";
import PrivateRoute from "./pages/PrivateRoute";

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Layout>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/blogs" element={<Blogs />} />
            <Route path="/blogs/:id" element={<SingleBlog />} />
            <Route path="/projects" element={<Projects />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/login" element={<Login />} />
            <Route path="/create-account" element={<SignUp />} />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <Profile />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin-dash"
              element={
                <PrivateRoute>
                  <AdminDashboard />
                </PrivateRoute>
              }
            />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Layout>
      </BrowserRouter>
    </AuthProvider>
  );
}
